import { useState, useEffect } from "react";
import { usePageMeta } from "@/lib/seo";
import { Link } from "react-router-dom";
import { Shield, Wrench, BarChart3, FileText, Users, Zap, ArrowRight, CheckCircle2, Award } from "lucide-react";
import api from "@/lib/api";
import { DirectoryPromo } from "@/components/directory/DirectoryPromo";
import { BannerSlot } from "@/components/BannerSlot";
import { PodcastPlayer } from "@/components/PodcastPlayer";
import "@/App.css";

const SHOP_ADVISOR_LOGO = "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/qy6rjh9d_Dynamic%20Logo%20for%20Automotive%20Channel%20Remix%20the%20Whip.png";

const PRODUCT_LOGOS = [
  { name: "ShopAdvisor", url: "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/qy6rjh9d_Dynamic%20Logo%20for%20Automotive%20Channel%20Remix%20the%20Whip.png" },
  { name: "The Stipulator", url: "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/758bncau_0795a352-3bc9-444f-9f6a-783ddd83ff27.png" },
  { name: "Dealer Advisor", url: "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/hmoruvd1_3b61c36d-1f8a-4e4e-9a27-3f4ac077ecca%20%281%29.png" },
  { name: "Lead Advisor", url: "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/kxsbja04_fbeaec0d-97ca-4737-abd7-ba518287da2f.png" },
  { name: "Marketing Advisor", url: "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/24zt27az_bdb85494-58c9-431b-9cb4-13d0ed3bb19c.png" },
  { name: "Contractor Advisor", url: "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/8lgmqlyg_58062f4e-f94d-4009-9911-0e7757c77a44.png" },
  { name: "Tech Advisors", url: "https://customer-assets.emergentagent.com/job_shop-advisor-demo/artifacts/mbie6gjy_9d3a610b-8838-4dad-b0eb-4ad119902c6d.png" },
];

const FEATURES = [
  { icon: <Wrench className="w-6 h-6" />, title: "All-in-One Shop OS", desc: "Estimates, customer dashboards, smart estimating, and complete workflow management.", checks: ["Estimate Management", "Customer Dashboards", "Smart Estimating"], link: "/demo?module=shop-advisor" },
  { icon: <BarChart3 className="w-6 h-6" />, title: "Shop Workflow Tracking", desc: "Track every job from disassembly through body, paint, reassembly, QC, to delivery.", checks: ["Blueprint & Progress Tracking", "Status Updates", "QC & Delivery"], link: "/demo?module=shop-advisor" },
  { icon: <Shield className="w-6 h-6" />, title: "Parts Inventory + Scanner", desc: "Complete parts ordering, inventory tracking with bins, barcodes, and returns management.", checks: ["NAPA & PartsTech", "Barcode Scanning", "Core Tracking"], link: "/demo?module=shop-advisor" },
  { icon: <Users className="w-6 h-6" />, title: "Lead Integration", desc: "Centralize leads from Yelp, Google, website forms. Auto-route to the right team.", checks: ["Yelp & Google Leads", "Website Forms", "Lead Routing"], link: "/demo?module=lead-advisor" },
  { icon: <Zap className="w-6 h-6" />, title: "Marketing Automation", desc: "Automated campaigns, review management, and customer engagement tools.", checks: ["Email & SMS Campaigns", "Review Management", "Analytics"], link: "/demo?module=marketing-advisor" },
  { icon: <FileText className="w-6 h-6" />, title: "Document System", desc: "Electronic deal jackets, contracts, compliance paperwork, and e-signatures.", checks: ["Deal Jacket Tracking", "E-signatures", "Audit Trail"], link: "/demo?module=the-stipulator" },
];

const PAIN_POINTS = [
  "Customers constantly calling for updates",
  "Disorganized photos & repair documentation",
  "Parts tracking mistakes & lost cores",
  "No centralized customer communication",
  "Leads from Yelp & Google not flowing into shop",
  "Outdated estimating workflows",
];

export default function HomePage() {
  usePageMeta("", "The ShopAdvisor™ — the complete checkpoint system for auto repair shops. Shop software, digital inspections, customer portal and AI website growth plans.");
  const [clientOfMonth, setClientOfMonth] = useState(null);

  useEffect(() => {
    api.get("/api/client-of-month").then(r => { if (r.data) setClientOfMonth(r.data); }).catch(() => {});
  }, []);


  return (
    <div className="min-h-screen bg-white pt-16">

      <BannerSlot placement="home" />

      {/* === HERO — logo inline with heading, left-aligned === */}
      <section className={`px-6 md:px-12 lg:px-24 pt-12 pb-10`} data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          {/* Logo + Heading row */}
          <div className="flex items-center gap-5 sm:gap-8 flex-wrap mb-5">
            <div className="overflow-hidden shrink-0" style={{ height: "70px", width: "310px", maxWidth: "60vw" }}>
              <img src={SHOP_ADVISOR_LOGO} alt="ShopAdvisor" className="w-full h-auto" style={{ marginTop: "-38%", marginBottom: "-38%" }} />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-slate-900 leading-none" data-testid="hero-headline">
              One Platform. <span className="text-blue-600">Total Control.</span>
            </h1>
          </div>
          <p className="text-base sm:text-lg text-slate-600 max-w-3xl mb-7 leading-relaxed">
            The complete operating system for auto repair shops, collision centers, dealerships, contractors, and service businesses. Manage everything from one dashboard.
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4">
            <Link to="/demo" className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 sm:px-8 py-3 rounded-lg transition-colors text-sm inline-flex items-center gap-2" data-testid="hero-demo-btn">Book a Demo <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/quote" className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 font-semibold px-6 sm:px-8 py-3 rounded-lg transition-colors text-sm" data-testid="hero-quote-btn">Request a Quote</Link>
            <Link to="/login" className="border border-slate-300 hover:border-blue-400 text-slate-700 hover:text-blue-600 font-medium px-6 sm:px-8 py-3 rounded-lg transition-colors text-sm" data-testid="hero-login-btn">Login</Link>
          </div>
          {/* Client of the Month */}
          {clientOfMonth && (
            <div className="mt-8 inline-flex items-center gap-4 bg-gradient-to-r from-blue-50 to-yellow-50 border border-blue-100 rounded-xl px-5 py-3" data-testid="client-of-month">
              <Award className="w-7 h-7 text-yellow-500 shrink-0" />
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 font-bold leading-none mb-0.5">Client of the Month</p>
                <p className="text-lg font-black text-slate-900 leading-tight">{clientOfMonth.name}{clientOfMonth.business_name ? ` — ${clientOfMonth.business_name}` : ""}</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* === PAIN POINTS === */}
      <section className="py-14 sm:py-20 px-6 md:px-12 lg:px-24 border-t border-slate-100 bg-slate-50" data-testid="pain-points-section">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-4">Auto body and repair shops <span className="text-red-500">struggle</span> with:</h2>
            <p className="text-slate-500 text-sm italic">Result: Lost time, lost profit, frustrated customers.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PAIN_POINTS.map((point, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <span className="text-slate-600 text-sm">{point}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === FEATURES GRID === */}
      <section className="py-14 sm:py-20 px-6 md:px-12 lg:px-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-blue-600 mb-2">All-in-One Shop Operating System</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900">Everything your shop needs. <span className="text-blue-600">One platform.</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <Link key={i} to={f.link} className="bg-white border border-slate-200 rounded-xl p-6 group hover:border-blue-400 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5" data-testid={`feature-card-${i}`}>
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-blue-600 mb-4 group-hover:bg-blue-100 transition-colors">{f.icon}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-slate-500 text-sm mb-3 leading-relaxed">{f.desc}</p>
                <div className="space-y-1.5">
                  {f.checks.map((c, j) => (
                    <div key={j} className="flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" /><span className="text-slate-600 text-xs">{c}</span></div>
                  ))}
                </div>
                <div className="mt-4 flex items-center gap-1.5 text-blue-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">View Demo <ArrowRight className="w-3.5 h-3.5" /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* === LOGO MARQUEE === */}
      <DirectoryPromo />
      <PodcastPlayer />
      <section className="py-12 border-t border-b border-slate-100 bg-slate-50 overflow-hidden" data-testid="logo-marquee">
        <p className="text-center text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-6">Powered by ShopAdvisor Ecosystem</p>
        <div className="relative">
          <div className="flex animate-marquee" style={{ width: "200%" }}>
            {[...PRODUCT_LOGOS, ...PRODUCT_LOGOS].map((logo, i) => (
              <div key={i} className="flex-shrink-0 mx-6 sm:mx-10 flex items-center justify-center overflow-hidden" style={{ width: "220px", height: "60px" }}>
                <img src={logo.url} alt={logo.name} className="w-full h-auto opacity-70 hover:opacity-100 transition-opacity" style={{ marginTop: "-35%", marginBottom: "-35%" }} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === CTA === */}
      <section className="py-16 sm:py-24 px-6 md:px-12 lg:px-24" data-testid="cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-5">Ready to digitize your shop?</h2>
          <p className="text-base sm:text-lg text-slate-500 mb-8 max-w-xl mx-auto">Join hundreds of shops using ShopAdvisor to streamline operations, boost revenue, and deliver exceptional customer experiences. <a href="https://www.theshopadvisor.com" className="text-blue-600 hover:text-blue-500 font-medium" target="_blank" rel="noopener noreferrer">www.theshopadvisor.com</a></p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/demo" className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-8 py-3.5 rounded-lg transition-colors inline-flex items-center gap-2" data-testid="cta-demo-btn">Book a Demo <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/pricing" className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 font-semibold px-8 py-3.5 rounded-lg transition-colors" data-testid="cta-pricing-btn">View Pricing</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
